import React, { Context, createContext, useEffect, useReducer, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { Header } from '@everlywell/leaves';
import { useLDClient } from 'gatsby-plugin-launchdarkly';

import GlobalStyle from 'styles/global';
import { ariesTheme } from 'styles/ariesTheme';
import { Theme, ThemeContext } from 'components/Themable';
import { getUser, resetSession } from 'utils/cartHelpers';
import ewAnalytics from 'utils/analytics';
import image from '../../images/logo/everlywell-logo.svg';
import * as S from './styles';
import loadable from '@loadable/component';
import { Spinner } from 'styles/utils';
import { initialState, reducer } from '../../containers/CartContainer/store';
import { setShowTOS } from '../../containers/CartContainer/store/actions';
import { MembershipStatus, ProductsBySlugType } from 'utils/types';
import { NotificationContextContainer } from '../../containers/Notification';

const { STORE_ROOT, RESULTS_ROOT } = require('utils/constants/urls');

declare global {
  interface Window {
    appRoot: string;
    asset_host: string;
  }
}

const Cart = loadable(() => import('../../containers/CartContainer'), {
  fallback: <Spinner />,
});

const Footer = loadable(() => import('../Footer'), {
  fallback: <Spinner />,
});

export interface UserType {
  activeCredits: number;
  fetched: boolean;
  firstName?: string;
  isMember: boolean;
  loggedIn: boolean;
  membershipType: string | null;
  nextCreditDate?: string;
  userEmail: string | null;
  userId: number | null;
  membershipState?: MembershipStatus;
}

type Props = {
  children: React.ReactNode;
  setParentUserData?: React.Dispatch<React.SetStateAction<UserType>>;
  productsBySlug?: ProductsBySlugType;
  viewEventId?: string;
  productId?: number;
};



interface CartContextType {
  isOpen: boolean;
  setTOS: Function;
}

export const defaultUser = {
  activeCredits: 0,
  fetched: false,
  firstName: undefined,
  isMember: false,
  loggedIn: false,
  membershipType: null,
  nextCreditDate: undefined,
  userEmail: null,
  userId: null,
  membershipState: undefined,
};


export const UserContext: Context<UserType> =
  createContext<UserType>(defaultUser);

export const CartContext: Context<CartContextType> =
  createContext<CartContextType>({
    setTOS: () => {
    }, isOpen: false,
  });

export const ProductInfoContext = createContext<ProductsBySlugType>({});

const accountLinks = {
  label: 'Account',
  results: {
    label: 'Test Results',
    url: `${RESULTS_ROOT}/dashboard`,
  },
  settings: {
    label: 'Settings',
    url: `${RESULTS_ROOT}/account/settings`,
  },
  logout: {
    label: 'Log Out',
    url: `${STORE_ROOT}/logout`,
  },
  membership: {
    label: 'My Membership',
  },
};

// backend response still returns `sti` as the membershipType on the user
// we need to map that to its actual name for use with creating links to the page
const membershipNames = {
  sti: 'current',
  control: 'control',
};

const Layout = (props: Props): JSX.Element => {
  const [userData, setUserData] = useState(defaultUser);
  const [cartState, dispatch] = useReducer(reducer, initialState);
  const { isOpen } = cartState;
  const cartProps = { cartState, dispatch };
  const headerRef = useRef<HTMLAnchorElement>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [subMenuOpen, setSubMenuOpen] = useState<boolean>(false);
  const { productsBySlug = {}, viewEventId, productId } = props;

  const ldClient = useLDClient();

  useEffect(() => {
    if (window) {
      /* Cart Global Variables */
      window.appRoot = '';
      /* Just Uno Init */
      // @ts-ignore
      window.ju_num = process.env.JUSTUNO_ACCOUNT_ID;
      window.asset_host = '//cdn.jst.ai/';
      (function(i, s, o, g, r, a, m) {
        // @ts-ignore
        i[r] =
          // @ts-ignore
          i[r] ||
          function() {
            // @ts-ignore
            (i[r].q = i[r].q || []).push(arguments); // eslint-disable-line
          };
        // @ts-ignore
        (a = s.createElement(o)), (m = s.getElementsByTagName(o)[0]);
        // @ts-ignore
        a.async = 1;
        // @ts-ignore
        a.src = g;
        // @ts-ignore
        m.parentNode.insertBefore(a, m);
        // @ts-ignore
      })(window, document, 'script', asset_host + 'vck.js', 'juapp'); // eslint-disable-line
    }

    const runEffect = async () => {
      try {
        const data = await getUser(viewEventId, productId);
        const sessionData = data ? data.data : {};
        const {
          logged_in: loggedIn,
          is_member: isMember,
          membership_type: membershipType,
          active_credits: activeCredits,
          user_id: userId,
          user_email: userEmail,
          next_credit_date: nextCreditDate,
          first_name: firstName,
          membership_state: membershipState
        } = sessionData;
        setUserData({
          loggedIn,
          isMember,
          membershipType,
          activeCredits,
          userId,
          nextCreditDate,
          firstName,
          userEmail,
          fetched: true,
          membershipState,
        });
        if (props.setParentUserData) {
          props.setParentUserData({
            loggedIn,
            isMember,
            membershipType,
            activeCredits,
            userId,
            nextCreditDate,
            firstName,
            userEmail,
            fetched: true,
            membershipState,
          });
        }

        ewAnalytics.setUserInfo({
          loggedIn,
          isMember,
          id: userId,
        });

        ldClient?.identify({
          key: 'guest',
          anonymous: true,
        });
      } catch (e) {
        setUserData({
          ...defaultUser,
          fetched: true,
        });

        ldClient?.identify({
          key: 'guest',
          anonymous: true,
        });
      }
    };
    runEffect();
  }, []);

  const setTOS = (isSet: boolean) => {
    setShowTOS(dispatch, isSet);
  };

  const handleTrackableLinkClick = (e: Event) => {
    ewAnalytics.eventHandler(e);
  };

  const handleLogout = (userId: string | null) => {
    if (userId) {
      ewAnalytics.setUserInfo({
        loggedIn: false,
        id: userId,
      });
    }
    resetSession();
  };

  // links defined in the Header have data-* attributes that get picked up in order to track via Mixpanel
  // the links aren't mounted when the header is closed, so we listen for the header drawer to open, and attach/remove listeners appropriately
  useEffect(() => {
    let trackableLinks: NodeListOf<HTMLAnchorElement> | undefined;

    if (menuOpen || subMenuOpen) {
      trackableLinks = headerRef?.current?.querySelectorAll(
        '[data-analytics-event]',
      );
      if (trackableLinks) {
        trackableLinks.forEach((link) =>
          link.addEventListener('click', handleTrackableLinkClick),
        );
      }
    }
    return () => {
      if (trackableLinks) {
        trackableLinks.forEach((link) =>
          link.removeEventListener('click', handleTrackableLinkClick),
        );
      }
    };
  }, [menuOpen, subMenuOpen]);

  return (
    <div id='app' className='siteRoot'>
      <GlobalStyle />
      <ThemeProvider theme={ariesTheme}>
        <ThemeContext.Provider value={Theme.Default}>
          <ProductInfoContext.Provider value={productsBySlug}>
            <UserContext.Provider value={userData}>
              {userData.fetched ? (
                <Header
                  loggedIn={userData.loggedIn}
                  logoSrc={image}
                  logoAlt='Everlywell'
                  id='everlywellHeader'
                  ref={headerRef}
                  cartComponent={<Cart {...cartProps} />}
                  accountLinks={accountLinks}
                  onMenuOpen={() => setMenuOpen(true)}
                  onMenuClose={() => setMenuOpen(false)}
                  onSubMenuOpen={() => setSubMenuOpen(true)}
                  onSubMenuClose={() => setSubMenuOpen(false)}
                  onLogout={() => handleLogout(userData.userId)}
                  activeMembership={
                    userData.membershipType
                      ? // @ts-ignore
                      membershipNames[userData.membershipType]
                      : undefined
                  }
                  numOfMemberCredits={userData.activeCredits}
                  membershipState={userData.membershipState}
                />
              ) : null}
              <S.Layout role='main'>
                <NotificationContextContainer>
                  <CartContext.Provider value={{ setTOS, isOpen }}>
                    {props.children}
                  </CartContext.Provider>
                  {isOpen ? <S.SolvvyHiddenStyles /> : <S.SolvvyOverrideStyles />}
                </NotificationContextContainer>
              </S.Layout>
              <Footer />
            </UserContext.Provider>
          </ProductInfoContext.Provider>
        </ThemeContext.Provider>
      </ThemeProvider>
    </div>
  );
};

export default Layout;
